/* AddImagesAction.js */

"use strict";
const AddImagesComponent = require("./AddImagesComponent");
const AddImagesSummaryComponent = require("./summary/AddImagesSummaryComponent");

/**
 * Action for adding images to IoT targets (Platforms, OS Plans, or Upgrade Plans).
 * Implements PE-13464: Add Images to IoT Platform Explorer
 *
 * Action Availability:
 * - Enabled when one or more targets are selected
 * - Disabled when no target is selected
 *
 * This action supports two modes:
 * 1. Editor mode: Single target entity is passed directly
 * 2. Explorer mode: Array of target IDs is passed, entities loaded from backend
 */
class AddImagesAction {
    constructor(logger, gameEditor, modalUserFeedback, OdmOsLink, OdmOsPlatform, userSession) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = modalUserFeedback;
        this._OdmOsLink = OdmOsLink;
        this._OdmOsPlatform = OdmOsPlatform;
        this._userSession = userSession;

        this.run = this.run.bind(this);
    }

    /**
     * Executes the Add Images action.
     * PE-13464: Opens veil to create new images or link existing images to selected targets.
     *
     * @returns {Promise} Promise that resolves when action completes
     */
    run() {
        // Get fresh activeView reference each time action runs
        const activeView = this._gameEditor._activeView;

        // Get target entity definition from activeView
        const targetEntityDef =
            activeView?._OdmOsPlatformEntityDef ||
            activeView?._OdmOsUpgradePlan ||
            activeView?._OdmOsPlatformEntity;

        if (!targetEntityDef) {
            this._logger.warn("AddImagesAction.run() - No target entity found");
            return Promise.resolve();
        }

        // Validate entity type
        const entityType = targetEntityDef.getEntityType();
        if (entityType !== "OdmOsPlatform" && entityType !== "OdmOsUpgradePlan") {
            this._logger.warn("AddImagesAction.run() - Invalid target entity type");
            return Promise.resolve();
        }

        // Detect editor mode: in editor, activeView has _odmOsPlatform or _plan property
        // In explorer, activeView has pagingFeed but no _odmOsPlatform
        const odmOsUpgradePlan = activeView?._plan;
        const odmOsPlatform = activeView?._odmOsPlatform;
        const isEditorMode = activeView && (odmOsPlatform || odmOsUpgradePlan) ? true : false;
        const selectedTarget = isEditorMode ? odmOsPlatform || odmOsUpgradePlan : null;

        let selectedTargets;
        if (isEditorMode) {
            // Editor mode: single target entity from activeView
            if (!selectedTarget) {
                this._logger.warn("AddImagesAction.run() - No target selected");
                return Promise.resolve();
            }
            selectedTargets = [selectedTarget.id];
        } else {
            // Explorer mode: get selected target IDs from active view
            selectedTargets =
                activeView && activeView.pagingFeed
                    ? activeView.getSelectedOdmOsPlatformIds().selectedRows || []
                    : [];

            // Only check length in explorer mode (array of IDs)
            if (selectedTargets.length === 0) {
                this._logger.warn("AddImagesAction.run() - No targets selected");
                return Promise.resolve();
            }
        }

        /**
         * In editor mode, load existing linked images first to exclude them from query
         * This prevents users from selecting already-linked images
         * Optimization: Use database-level filtering instead of in-memory filtering
         */
        const loadPromise = isEditorMode
            ? selectedTarget.loadRelatedLinks({}, {id: true}).then((existingLinkedImages) => {
                  // Extract IDs of already-linked images for query exclusion
                  const existingLinkedImageIds = existingLinkedImages.map((image) => image.id);

                  // Build query to exclude already-linked images at database level
                  const query =
                      existingLinkedImageIds.length > 0 ? {id: {$nin: existingLinkedImageIds}} : {};

                  // Load only unlinked images from database
                  return this._OdmOsLink.loadOdmOsLinks(this._gameEditor._activeOrgId, query);
              })
            : this._OdmOsLink.loadOdmOsLinks(this._gameEditor._activeOrgId);

        return loadPromise.then((odmOsLinks) => {
            const addImagesComponent = new AddImagesComponent(
                this._logger,
                selectedTargets,
                odmOsLinks,
                this._OdmOsLink,
                targetEntityDef,
                this._gameEditor._activeOrgId,
                this._userSession,
                isEditorMode
            );
            let res;
            return this._modalUserFeedback
                .displayCustom(addImagesComponent, {dontHide: true})
                .then((data) => {
                    // Hide the Add Images veil
                    res = data;
                    return this._modalUserFeedback.hideDisplayed();
                })
                .then(() => {
                    // Check if there were errors or skipped items during linking
                    if (res && !res.success && (res.failureCount > 0 || res.skippedCount > 0)) {
                        // Show summary veil with details
                        const summaryData = {
                            totalImages: res.totalImages,
                            totalTargets: res.totalTargets,
                            total: res.total,
                            successCount: res.successCount,
                            failureCount: res.failureCount,
                            skippedCount: res.skippedCount,
                            errors: res.errors,
                        };

                        const addImagesSummaryComponent = new AddImagesSummaryComponent(
                            summaryData,
                            this._logger
                        );

                        return this._modalUserFeedback
                            .displayCustom(addImagesSummaryComponent)
                            .then(() => {
                                // After summary veil closes, refresh grid if any images were linked
                                if (res.successCount > 0) {
                                    this._refreshGrid();
                                }
                            });
                    } else {
                        // Success - refresh grid
                        this._refreshGrid();
                    }

                    return res;
                })
                .catch((err) => {
                    // User cancelled or error
                    this._modalUserFeedback.hideDisplayed();
                    if (!err.userRejected) {
                        this._logger.warn(
                            {error: err},
                            "AddImagesAction.run() - Error during dialog display"
                        );
                    }
                });
        });
    }

    /**
     * Refreshes the grid to update Image Count column
     * @private
     */
    _refreshGrid() {
        // Get fresh activeView reference
        const activeView = this._gameEditor._activeView;
        if (activeView && activeView.pagingFeed && activeView.pagingFeed.feedUpdate) {
            activeView.pagingFeed.feedUpdate();
        }
    }
}

module.exports = AddImagesAction;

AddImagesAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "$teve.entities.OdmOsLink",
    "$teve.entities.OdmOsPlatform",
    "$teve.userSession",
];

AddImagesAction.$displayDetails = {
    type: "Action",
    name: "AddImagesAction",
    icon: "osmanager.add_images",
    shown: true,
    title: "Add Images",
    order: 3,
};
